import { supabase } from '@/lib/supabase'
import { normalizeError } from './errors'
import type {
  Gallery,
  GalleryTag,
  GalleryListItem,
  GalleryQueryParams
} from '../types'

export type { GalleryListItem }

export async function fetchGalleries(params: GalleryQueryParams): Promise<{
  galleries: GalleryListItem[]
  totalCount: number
  page: number
  pageSize: number
}> {
  try {
    const {
      page,
      pageSize,
      searchTerm,
      regions = [],
      statuses = [],
      tags = [],
      services = [],
      publicOnly,
      socialOnly,
      needsJobDetailsSync,
      sortBy = 'created_on',
      sortDir = 'desc'
    } = params

    const from = (page - 1) * pageSize
    const to = from + pageSize - 1

    const jobberDetailsSelect = 'gallery_jobber_details(enrichment_status,fetched_on,service_city,service_province)'

    // Build selectFields based on active filters
    // Using !inner join for filtering means we need to include those relations
    let selectFields = `*, ${jobberDetailsSelect}`
    if (tags.length > 0) {
      selectFields = `*, gallery_tag_relations!inner(tag_id), ${jobberDetailsSelect}`
    }
    if (services.length > 0) {
      // Add gallery_services join for service filtering
      selectFields = tags.length > 0
        ? `*, gallery_tag_relations!inner(tag_id), gallery_services!inner(service_id), ${jobberDetailsSelect}`
        : `*, gallery_services!inner(service_id), ${jobberDetailsSelect}`
    }

    let query = supabase
      .from('galleries')
      .select(selectFields, { count: 'estimated' })
      .order(sortBy, { ascending: sortDir === 'asc' })

    const term = (searchTerm || '').trim()
    if (term) {
      const cleanTerm = term.replace(/[#]/g, '')
      const isNumeric = /^\d+$/.test(cleanTerm)
      const like = `%${term}%`
      query = query.or(
        isNumeric
          ? `job_number.eq.${cleanTerm},title.ilike.${like},description.ilike.${like}`
          : `title.ilike.${like},description.ilike.${like}`
      )
    }

    if (regions.length > 0) query = query.in('franchise_region', regions)
    if (statuses.length > 0) query = query.in('review_status', statuses)
    if (tags.length > 0) query = query.in('gallery_tag_relations.tag_id', tags)
    if (services.length > 0) query = query.in('gallery_services.service_id', services)
    if (publicOnly) query = query.eq('is_public', true)
    if (socialOnly) query = query.eq('used_for_social_media', true)

    // Filter for galleries needing job details sync
    // Must have job_number but no corresponding jobber_job_details entry
    if (needsJobDetailsSync) {
      // First, get all synced job numbers from jobber_job_details
      const { data: syncedJobs } = await supabase
        .from('jobber_job_details')
        .select('job_number')

      const syncedJobNumbers = new Set((syncedJobs || []).map(j => j.job_number))

      // Must have a job_number
      query = query.not('job_number', 'is', null)

      // If we have synced jobs, exclude them
      if (syncedJobNumbers.size > 0) {
        const syncedArray = Array.from(syncedJobNumbers)
        query = query.not('job_number', 'in', `(${syncedArray.join(',')})`)
      }
    }

    const { data, error, count } = await query.range(from, to)
    if (error) throw error
    return { galleries: (data || []) as unknown as GalleryListItem[], totalCount: count || 0, page, pageSize }
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch galleries')
  }
}

export async function fetchGalleryById(id: number): Promise<Gallery> {
  try {
    const { data, error } = await supabase.from('galleries').select('*').eq('id', id).single()
    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch gallery')
  }
}

/**
 * Set the cover image for a gallery.
 * Clears all existing is_cover flags and sets the new one.
 *
 * @param id - Gallery ID
 * @param imageUrl - URL of the new cover image
 * @param jobImageId - Optional: The gallery_job_images.id to mark as cover
 */
export async function setCoverImage(
  id: number,
  imageUrl: string,
  jobImageId?: number
): Promise<Gallery> {
  try {
    // 1. Clear ALL existing is_cover flags for this gallery
    // This prevents multiple images showing "Cover" badge
    const { error: clearError } = await supabase
      .from('gallery_job_images')
      .update({ is_cover: false })
      .eq('gallery_id', id)

    if (clearError) {
      console.warn('Failed to clear is_cover flags:', clearError)
    }

    // 2. If we have a specific jobImageId, set it as cover
    if (jobImageId) {
      const { error: setError } = await supabase
        .from('gallery_job_images')
        .update({ is_cover: true })
        .eq('id', jobImageId)
        .eq('gallery_id', id)

      if (setError) {
        console.warn('Failed to set is_cover flag:', setError)
      }
    }

    // 3. Update the gallery's cover_image URL
    const { data, error } = await supabase
      .from('galleries')
      .update({ cover_image: imageUrl })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to set cover image')
  }
}

export async function updateGallery(id: number, patch: Partial<Gallery>): Promise<Gallery> {
  try {
    const { data, error } = await supabase.from('galleries').update(patch).eq('id', id).select().single()
    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to update gallery')
  }
}

export async function deleteGallery(id: number): Promise<void> {
  try {
    const { error } = await supabase.from('galleries').delete().eq('id', id)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to delete gallery')
  }
}

export async function createTag(name: string, description?: string): Promise<GalleryTag> {
  try {
    const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    const { data, error } = await supabase
      .from('gallery_tags')
      .insert({ name, slug, description: description || '' })
      .select()
      .single()
    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to create tag')
  }
}

export async function addTagToGallery(galleryId: number, tagId: string): Promise<void> {
  try {
    const { error } = await supabase.from('gallery_tag_relations').insert({ gallery_id: galleryId, tag_id: tagId })
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to add tag to gallery')
  }
}

export async function removeTagFromGallery(galleryId: number, tagId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('gallery_tag_relations')
      .delete()
      .eq('gallery_id', galleryId)
      .eq('tag_id', tagId)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to remove tag from gallery')
  }
}

// Tags list
export async function fetchTags(): Promise<GalleryTag[]> {
  try {
    const { data, error } = await supabase.from('gallery_tags').select('*').order('name', { ascending: true })
    if (error) throw error
    return data || []
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch tags')
  }
}

export async function trackGalleryImageUsage(galleryId: number, imageIds: number[] = []): Promise<void> {
  try {
    const { error } = await supabase
      .from('galleries')
      .update({ used_for_social_media: true })
      .eq('id', galleryId)
    if (error) throw error

    if (imageIds.length) {
      console.debug(`Tracked gallery ${galleryId} usage for images:`, imageIds)
    }
  } catch (err) {
    throw normalizeError(err, 'Failed to track gallery social usage')
  }
}

// Bulk operations for management page toolbar
export async function bulkUpdateStatus(
  ids: number[],
  status: 'approved' | 'rejected' | 'under_review'
): Promise<void> {
  if (!ids || ids.length === 0) return
  try {
    const { error } = await supabase
      .from('galleries')
      .update({ review_status: status })
      .in('id', ids)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to update review status')
  }
}

export async function bulkSetPublic(ids: number[], isPublic: boolean): Promise<void> {
  if (!ids || ids.length === 0) return
  try {
    const { error } = await supabase
      .from('galleries')
      .update({ is_public: isPublic })
      .in('id', ids)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to update public flag')
  }
}

export async function bulkDelete(ids: number[]): Promise<void> {
  if (!ids || ids.length === 0) return
  try {
    const { error } = await supabase
      .from('galleries')
      .delete()
      .in('id', ids)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to delete galleries')
  }
}
